<template>
  <div class="px-4 px-lg-12 py-12 d-flex flex-column align-center" style="height: 100%; width: 100%">
    <div class="text-h5 font-weight-bold mb-10 text-center">Institute Details</div>

    <v-form @submit.prevent="submit" ref="form">
      <v-row class="no-v-gutter">
        <v-col cols="12">
          <v-text-field
              :rules="[rules.required]"
              outlined
              prepend-inner-icon="mdi-form-textbox"
              v-model="instituteName"
          >
            <template #label>Institute Name *</template>
          </v-text-field>
        </v-col>
        <v-col cols="12" v-if="!useSubDomain">
          <v-alert
              class="text-body-2"
              dark
              dense
              prominent
              type="warning"
          >Please let us know to setup the domain for you via 0717705526. You can continue the registration by entering your domain
          here</v-alert>
        </v-col>
        <v-col class="d-flex align-baseline" cols="12">
          <v-text-field
              :error-messages="domainExistErrMsg"
              :loading="loadingDomainField"
              :rules="[rules.required, rules.subDomain]"
              :suffix="parentDomain"
              @blur="checkDomainValidity"
              outlined
              placeholder="abc"
              prepend-inner-icon="mdi-earth"
              v-if="useSubDomain"
              v-model="subDomain"
          >
            <template #label>Sub Domain Name *</template>
          </v-text-field>
          <v-text-field
              :error-messages="domainExistErrMsg"
              :loading="loadingDomainField"
              :rules="[rules.required, rules.domain]"
              @blur="checkDomainValidity"
              outlined
              placeholder="example.com"
              prepend-inner-icon="mdi-earth"
              v-else
              v-model="domain"
          >
            <template #label>Domain Name *</template>
          </v-text-field>
          <v-tooltip bottom color="info">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  :color="useSubDomain ? '':'primary'"
                  @click="switchDomainField"
                  class="ml-1"
                  icon
                  large
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-swap-horizontal</v-icon>
              </v-btn>
            </template>
            <span>If you have your own domain, use this switch to enter your domain</span>
          </v-tooltip>
        </v-col>
        <v-col cols="12">
          <v-text-field
              :rules="[rules.required]"
              outlined
              prepend-inner-icon="mdi-map-marker"
              type="text"
              v-model="addressLine1"
          >
            <template #label>Address Line 1 *</template>
          </v-text-field>
        </v-col>
        <v-col cols="12">
          <v-text-field
              :rules="[rules.required]"
              outlined
              prepend-inner-icon="mdi-map-marker-outline"
              type="text"
              v-model="addressLine2"
          >
            <template #label>Address Line 2 *</template>
          </v-text-field>
        </v-col>
        <v-col cols="12" lg="6">
          <v-text-field label="City" outlined prepend-inner-icon="mdi-city" type="text" v-model="city"/>
        </v-col>
        <v-col cols="12" lg="6">
          <v-text-field
              label="Postal Code"
              outlined
              prepend-inner-icon="mdi-mailbox-open"
              type="text"
              v-model="postalCode"
          />
        </v-col>
        <v-col cols="12">
          <v-select
              :items="institutePackages"
              :messages="packageMessage"
              :rules="[rules.required]"
              item-text="name"
              item-value="id"
              outlined
              prepend-inner-icon="mdi-package-variant"
              v-model="packageId"
          >
            <template #label>Package *</template>
          </v-select>
          <v-btn class="mb-6" color="primary" href="https://klass.lk/pricing" plain target="_blank" x-small>
            <v-icon left>mdi-information</v-icon>
            Learn more about packages
          </v-btn>
        </v-col>
        <v-col cols="12">
          <div class="text-subtitle-1 text--secondary mb-1">Institute Logo</div>
          <image-crop-upload @crop="onLogoCropped" placeholder="Select institute logo"/>
        </v-col>
      </v-row>

      <v-subheader class="text-h6 pl-0 mt-9 mb-4">Admin Details</v-subheader>
      <v-row class="no-v-gutter">
        <v-col cols="12" lg="6">
          <v-text-field :rules="[rules.required]" outlined prepend-inner-icon="mdi-form-textbox" v-model="firstName">
            <template #label>First Name *</template>
          </v-text-field>
        </v-col>
        <v-col cols="12" lg="6">
          <v-text-field :rules="[rules.required]" outlined prepend-inner-icon="mdi-form-textbox" v-model="lastName">
            <template #label>Last Name *</template>
          </v-text-field>
        </v-col>
        <v-col cols="12">
          <v-text-field
              :append-icon="revealPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :rules="[rules.required, rules.min]"
              :type="revealPassword ? 'text' : 'password'"
              @click:append="revealPassword = !revealPassword"
              autocomplete="new-password"
              outlined
              prepend-inner-icon="mdi-lock"
              v-model="password"
          >
            <template #label>Password *</template>
          </v-text-field>
        </v-col>
      </v-row>

      <v-checkbox
          label="I am a Teacher"
          messages="Check this if you are a teacher in the institute"
          v-model="isTeacher"
      />
      <v-btn :loading="isSubmitting" block class="mt-5 mb-8" color="primary" large type="submit">Submit</v-btn>
      <v-btn :disabled="isSubmitting" block class="mt-2" text color="error" large type="button" @click="onCancel">
        Cancel
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import {environment} from "@/config/environment";
import {rules} from "@/utils/validatorUtil";
import {instituteService, packageService} from "@/services";
import ImageCropUpload from "@/components/ImageCropUpload.vue";

export default {
  name: "InstituteDetails",

  components: {ImageCropUpload},

  data: () => ({
    // form data
    instituteName: "",
    subDomain: "",
    domain: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    postalCode: "",
    mobileNumber: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    packageId: "",
    isTeacher: "",
    logoAsFile: null,
    // other data
    isSubmitting: false,
    revealPassword: false,
    loadingDomainField: false,
    useSubDomain: true,
    domainExistErrMsg: "",
    packageMessage: "",
    institutePackages: [],
  }),

  computed: {
    rules() {
      return rules;
    },

    parentDomain() {
      return "." + environment.parentDomainName;
    },

    newRegistration() {
      return JSON.parse(sessionStorage.getItem("newRegistration") || "{}");
    },
  },

  watch: {
    packageId(newVal) {
      if (newVal) {
        const selectedPackage = this.institutePackages.find((el) => el.id === newVal);
        const feePerStudent = selectedPackage.perStudent
            ? `+ ${selectedPackage.perStudent} per student (Only when student count exceed ${selectedPackage.students})`
            : "";
        this.packageMessage = `Total Fee (LKR) = ${selectedPackage.monthlyFee} ${feePerStudent}`;
      } else {
        this.packageMessage = "";
      }
    },
  },

  mounted() {
    this.getPackages();
  },

  methods: {
    async getPackages() {
      try {
        const response = await packageService.getPackages();
        this.institutePackages = response.data;
      } catch (error) {
        console.log(error);
      }
    },

    async submit() {
      if (this.$refs.form.validate()) {
        const requestData = {
          instituteName: this.instituteName?.trim(),
          firstName: this.firstName?.trim(),
          lastName: this.lastName?.trim(),
          email: this.newRegistration?.email?.trim(),
          subDomain: this.subDomain?.trim(),
          domain: this.domain?.trim(),
          password: this.password?.trim(),
          mobileNumber: this.newRegistration?.phone?.trim(),
          address: {
            line1: this.addressLine1?.trim(),
            line2: this.addressLine2?.trim(),
            city: this.city?.trim(),
            postalCode: this.postalCode?.trim(),
          },
          packageId: this.packageId,
          isTeacher: !!this.isTeacher,
          registrationCode: this.newRegistration?.registrationCode?.trim(),
          referralCode: this.newRegistration?.referralCode?.trim(),
        };

        const meta = JSON.stringify(requestData);
        const data = new FormData();
        if (this.logoAsFile) {
          data.append("logo", this.logoAsFile);
        }
        data.append("meta", new Blob([meta], {type: "application/json"}));

        try {
          this.isSubmitting = true;
          const response = await instituteService.register(data);

          // update newRegistration object
          const newRegistration = JSON.stringify({
            ...this.newRegistration,
            status: "SUCCESS",
            id: response.data.id,
            url: "http://" + (response.data.subDomain || response.data.domain),
          });
          sessionStorage.setItem("newRegistration", newRegistration);

          this.$router.push({ name: "Success" });
        } catch (error) {
          this.$snackbar.show({
            message: error?.response?.data?.message,
            color: this.$colors.error,
            horizontal: "right",
          });
        } finally {
          this.isSubmitting = false;
        }
      }
    },

    async checkDomainValidity(e) {
      this.domainExistErrMsg = "";
      if (e.target.value) {
        try {
          this.loadingDomainField = true;
          const response = await instituteService.checkDomainValidity(e.target.value);
          if (response.data) {
            this.domainExistErrMsg = "Domain already exist";
          }
        } catch (error) {
          console.log(error);
        } finally {
          this.loadingDomainField = false;
        }
      }
    },

    onLogoCropped({blobToFile}) {
      this.logoAsFile = blobToFile;
    },

    switchDomainField() {
      this.useSubDomain = !this.useSubDomain;
      this.domainExistErrMsg = "";
      if (this.useSubDomain) {
        this.domain = "";
      } else {
        this.subDomain = "";
      }
    },
    onCancel() {
      sessionStorage.removeItem("newRegistration");
      location.reload();
    }
  },
};
</script>
